import {useQuery} from 'react-query'
import {parseISO} from 'date-fns'

import {
  api,
  queryClient,
  replaceQueryClientDataItem,
  useMutationQuery,
} from 'base'
import {showSuccessNotification, showErrorNotification} from 'common/helpers'
import {
  FileInterface,
  BlobInterface,
  FilesCategory,
  ProjectFilesCategoryValues,
} from 'common/types'

const isErrorPreUpload = (files: FileList) => {
  // console.log(files)
  let msg = ''
  if (!files?.length) {
    msg = 'No valid file!'
  }
  else if (files.length > 20) {
    msg = 'Too many files! Please select files no more than 20.'
  }
  else {
    const totalSize = Array.from(files).reduce((accu:number, f:File) => {
      return accu += f.size
    }, 0)
    // console.log(totalSize)
    if (totalSize > 200000000) {
      msg = 'To large to upload! Total size no more than 200MB.'
    }
  }
  if (!!msg) {
    showErrorNotification(msg)
  }
  return !!msg
}

interface UseUploadFileParams {
  key?: string
  setFile?: (file: FileInterface) => void
  attachFile?: (fileUuid: string) => void
}

interface UseUploadFilesParams {
  key?: string
  setFiles?: (files: FileInterface[]) => void
  attachFiles?: (filesUuids: string[]) => void
  isCreateDrawdown?: boolean
}

const syncProjectFilesCategoriesAfterUpdate = (
  projectUuid: string,
  data: FilesCategory
) => {
  const requestKey = ['projectFilesCategories', projectUuid]
  replaceQueryClientDataItem(requestKey, data)
  queryClient.invalidateQueries(requestKey)
}

export const useUploadFile = ({
  key,
  setFile,
  attachFile,
}: UseUploadFileParams) =>
  useMutationQuery<{blob: BlobInterface; fileUuid: string}, any, BlobInterface>(
    ['uploadFile', key],
    async (blob) => {
      const formData = new FormData()
      formData.append('file', blob)
      const {data} = await api.post('/file/upload', formData)
      return {blob, fileUuid: data.files[0]}
    },
    {
      onSuccess({blob, fileUuid}) {
        if (setFile) {
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onload = () =>
            setFile({
              blob,
              uuid: fileUuid,
              url: reader.result as any,
            })
          return
        }
        attachFile(fileUuid)
      },
    }
  )

export const useUploadFiles = ({
  key,
  setFiles,
  attachFiles,
  // 用于为createDrawdown的保存草稿的功能
  isCreateDrawdown
}: UseUploadFilesParams) =>
  useMutationQuery<
    {files: BlobInterface[]; filesUuids: string[]},
    any,
    FileList
  >(
    ['uploadFiles', key],
    async (fileList) => {
      if (isErrorPreUpload(fileList)) {
        return { files: null, filesUuids: null }
      }
      const formData = new FormData()
      isCreateDrawdown && formData.append('type', 'drawdown')
      const files = Array.from(fileList)
      files.forEach((file) => formData.append('files[]', file))
      const {data} = await api.post('/file/upload', formData)
      return {files, filesUuids: data.files}
    },
    {
      onSuccess({files, filesUuids}) {
        if (!files) return
        // else
        if (setFiles) {
          const newFiles: FileInterface[] = []
          files.forEach((file, index) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            // create drawdown 返回的格式不同
            reader.onload = () => {
              newFiles.push({
                blob: file,
                uuid: isCreateDrawdown ? (filesUuids[index] as any).uuid: filesUuids[index],
                url: isCreateDrawdown ? (filesUuids[index] as any).url : reader.result as any,
              })
              if (newFiles.length !== files.length) return
              setFiles(newFiles)
            }
          })
          return
        }
        attachFiles(filesUuids)
      },
    }
  )

export const useGetProjectChatFiles = (projectUuid: string) =>
  useQuery<any>(['projectChatFiles', projectUuid], async () => {
    const {data} = await api.get(`/chat/project/${projectUuid}/files`)
    return data.data
  })

export const useGetProjectFilesCategories = (projectUuid: string) =>
  useQuery<FilesCategory[]>(
    ['projectFilesCategories', projectUuid],
    async () => {
      const {data} = await api.get('/projectFilesCategory/list', {
        params: {
          project: projectUuid,
        },
      })
      const sortedData: FilesCategory[] = data.data.slice()
      sortedData.sort((a, b) => +parseISO(a.createdAt) - +parseISO(b.createdAt))
      return sortedData
    }
  )

export const useCreateProjectFilesCategory = (
  projectUuid: string,
  onClose: () => void
) =>
  useMutationQuery<any, any, ProjectFilesCategoryValues>(
    ['createProjectFilesCategory', projectUuid],
    async (values) => {
      const {data} = await api.post('/projectFilesCategory/new', {
        ...values,
        project: projectUuid,
      })
      return data
    },
    {
      onSuccess({data}) {
        const requestKey = ['projectFilesCategories', projectUuid]
        queryClient.setQueriesData(requestKey, (oldData: FilesCategory[]) => {
          const newData = oldData.slice()
          newData.push(data)
          return newData
        })
        queryClient.invalidateQueries(requestKey)
        showSuccessNotification('Files category has been created.')
        onClose()
      },
    }
  )

export const useUpdateProjectFilesCategory = (
  projectUuid: string,
  categoryUuid: string,
  onClose: () => void
) =>
  useMutationQuery<any, any, ProjectFilesCategoryValues>(
    ['updateProjectFilesCategory', categoryUuid],
    async (values) => {
      const {data} = await api.post(
        `/projectFilesCategory/${categoryUuid}`,
        values
      )
      return data
    },
    {
      onSuccess({data}) {
        syncProjectFilesCategoriesAfterUpdate(projectUuid, data)
        showSuccessNotification('Category has been updated.')
        onClose()
      },
    }
  )

export const useDeleteProjectFilesCategory = (
  projectUuid: string,
  categoryUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['deleteProjectFilesCategory', categoryUuid],
    async (categoryUuid) => {
      const {data} = await api.delete(`/projectFilesCategory/${categoryUuid}`)
      return data
    },
    {
      onSuccess(_, categoryUuid) {
        const requestKey = ['projectFilesCategories', projectUuid]
        queryClient.setQueriesData(requestKey, (oldData: FilesCategory[]) =>
          oldData.filter((category) => category.uuid !== categoryUuid)
        )
        queryClient.invalidateQueries(requestKey)
        showSuccessNotification('Category has been deleted.')
      },
    }
  )

export const useAttachProjectFile = (
  projectUuid: string,
  categoryUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['attachProjectFile', categoryUuid],
    async (fileUuid) => {
      const {data} = await api.post(
        `/projectFilesCategory/${categoryUuid}/attach`,
        {
          files: [fileUuid],
        }
      )
      return data
    },
    {
      onSuccess({data}) {
        syncProjectFilesCategoriesAfterUpdate(projectUuid, data)
      },
    }
  )

export const useRemoveProjectFile = (
  projectUuid: string,
  categoryUuid: string,
  fileUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['removeProjectFile', categoryUuid, fileUuid],
    async (fileUuid) => {
      const {data} = await api.delete(
        `/projectFilesCategory/${categoryUuid}/remove`,
        {
          data: {
            files: [fileUuid],
          },
        }
      )
      return data
    },
    {
      onSuccess({data}) {
        syncProjectFilesCategoriesAfterUpdate(projectUuid, data)
      },
    }
  )
